import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { adminUsersApi, AdminOrderSummary } from '../services/adminUsersApi';
import { ordersApi, VehicleResponse } from '../services/ordersApi';
import type { AdminUserDetailResponse, CustomerTermsAcceptance, GarageSummary } from '../types';
import { useAuth } from '../hooks/useAuth';
import CursorPagination from '../components/CursorPagination';
import UsernameChip from '../components/UsernameChip';
import AdminActionsCard from '../components/audit/AdminActionsCard';

/* ─── Helpers ─── */
function Field({ label, value }: { label: string; value: string | undefined | null }) {
  return (
    <div>
      <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontWeight: 500, color: 'var(--admin-text)' }}>{value || 'Not provided'}</div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const cls = status === 'BLOCKED' ? 'rejected' : 'approved';
  return <span className={`admin-badge ${cls}`}>{status}</span>;
}

function RoleBadge({ userType }: { userType: string }) {
  const map: Record<string, { cls: string; label: string }> = {
    ADMIN: { cls: 'admin', label: 'Admin' },
    GARAGE_OWNER: { cls: 'garage-owner', label: 'Garage Owner' },
    GARAGE_MANAGER: { cls: 'garage-owner', label: 'Manager' },
    GARAGE_SUPPORT: { cls: 'garage-owner', label: 'Staff' },
    CUSTOMER: { cls: 'approved', label: 'Customer' },
  };
  const { cls, label } = map[userType] || { cls: '', label: userType };
  return <span className={`admin-badge ${cls}`}>{label}</span>;
}

function OrderStatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    APPROVED: 'approved', COMPLETED: 'approved', SETTLED: 'approved', CAR_RETURNED: 'approved',
    CANCELLED: 'rejected', REFUNDED: 'rejected',
    PENDING: 'pending', PICKUP_SCHEDULED: 'pending', IN_SERVICE: 'pending',
    CAR_PICKED_UP: 'pending', CAR_DELIVERED_TO_GARAGE: 'pending',
  };
  return <span className={`admin-badge ${map[status] || 'pending'}`}>{status.replace(/_/g, ' ')}</span>;
}

const ORDER_STATUSES = [
  'APPROVED', 'PICKUP_SCHEDULED', 'CAR_PICKED_UP', 'CAR_DELIVERED_TO_GARAGE',
  'IN_SERVICE', 'COMPLETED', 'CAR_RETURNED', 'CANCELLED', 'SETTLED', 'REFUNDED',
];

/* ─── Main Component ─── */
export default function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const auth = useAuth();

  const [user, setUser] = useState<AdminUserDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Block/unblock state
  const [showBlockForm, setShowBlockForm] = useState(false);
  const [blockReason, setBlockReason] = useState('');
  const [blockLoading, setBlockLoading] = useState(false);
  const [showBlockWarning, setShowBlockWarning] = useState(false);
  const [blockWarningData, setBlockWarningData] = useState<{ activeOrderCount: number; garageNames: string[] } | null>(null);

  // Password reset
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  // Linked Garages (GARAGE_OWNER)
  const [garages, setGarages] = useState<GarageSummary[]>([]);
  const [garagesLoading, setGaragesLoading] = useState(false);

  // Order History (CUSTOMER)
  const [orders, setOrders] = useState<AdminOrderSummary[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersLoadingMore, setOrdersLoadingMore] = useState(false);
  const [ordersNextCursor, setOrdersNextCursor] = useState<string | undefined>();
  const [ordersHasMore, setOrdersHasMore] = useState(false);
  const [orderSearch, setOrderSearch] = useState('');
  const [orderStatusFilter, setOrderStatusFilter] = useState('');
  const [orderDateFrom, setOrderDateFrom] = useState('');
  const [orderDateTo, setOrderDateTo] = useState('');

  // Vehicles (CUSTOMER)
  const [vehicles, setVehicles] = useState<VehicleResponse[]>([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);

  // Terms acceptance (CUSTOMER, #1165) — the profile's termsAcceptedAt is partner-onboarding only
  const [termsAcceptances, setTermsAcceptances] = useState<CustomerTermsAcceptance[]>([]);
  const [termsLoading, setTermsLoading] = useState(false);
  const [termsError, setTermsError] = useState<string | null>(null);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchUser = useCallback(() => {
    if (!auth.token || !id) return;
    setLoading(true);
    const token = auth.token;
    adminUsersApi.getUserDetail(id, token)
      .then(setUser)
      // The :id may be a Keycloak subjectId (e.g. when navigating from an order's customer
      // link, where order.customerId is the subject) rather than the users-service PK — the
      // by-id lookup then 404s. Fall back to the by-subject lookup before surfacing an error.
      .catch(() => adminUsersApi.getUserDetailBySubject(id, token).then(setUser))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [auth.token, id]);

  useEffect(() => { fetchUser(); }, [fetchUser]);

  // Load role-specific data when user loads — every partner-role user belongs to a
  // garage (OWNER directly, GARAGE_SUPPORT/GARAGE_MANAGER as a member), so fetch the
  // Linked Garages panel for all of them.
  useEffect(() => {
    if (!user || !auth.token) return;
    const isPartnerRole = user.userType === 'GARAGE_OWNER'
        || user.userType === 'GARAGE_SUPPORT'
        || user.userType === 'GARAGE_MANAGER';
    if (isPartnerRole) {
      setGaragesLoading(true);
      adminUsersApi.getUserGarages(user.subjectId, auth.token)
        .then(setGarages)
        .catch(() => {})
        .finally(() => setGaragesLoading(false));
    }
  }, [user?.userType, user?.subjectId, auth.token]);

  // Orders fetch for CUSTOMER
  const fetchOrders = useCallback((cursor?: string) => {
    if (!auth.token || !user || user.userType !== 'CUSTOMER') return;
    const isMore = !!cursor;
    if (isMore) setOrdersLoadingMore(true); else setOrdersLoading(true);
    adminUsersApi.getUserOrders({
      customerId: user.id,
      search: orderSearch || undefined,
      status: orderStatusFilter || undefined,
      dateFrom: orderDateFrom || undefined,
      dateTo: orderDateTo || undefined,
      cursor,
      size: 20,
    }, auth.token)
      .then((data) => {
        if (isMore) setOrders(prev => [...prev, ...data.content]);
        else setOrders(data.content);
        setOrdersNextCursor(data.nextCursor || undefined);
        setOrdersHasMore(data.hasMore);
      })
      .catch(() => {})
      .finally(() => { setOrdersLoading(false); setOrdersLoadingMore(false); });
  }, [auth.token, user?.id, user?.userType, orderSearch, orderStatusFilter, orderDateFrom, orderDateTo]);

  useEffect(() => {
    if (user?.userType === 'CUSTOMER') {
      const t = setTimeout(() => fetchOrders(), 300);
      return () => clearTimeout(t);
    }
  }, [fetchOrders]);

  // Vehicles fetch for CUSTOMER
  useEffect(() => {
    if (!auth.token || !user || user.userType !== 'CUSTOMER') return;
    setVehiclesLoading(true);
    ordersApi.getCustomerVehicles(user.id, auth.token)
      .then(setVehicles)
      .catch(() => setVehicles([]))
      .finally(() => setVehiclesLoading(false));
  }, [auth.token, user?.id, user?.userType]);

  // Terms acceptances fetch for CUSTOMER (#1165)
  useEffect(() => {
    if (!auth.token || !user || user.userType !== 'CUSTOMER') return;
    let cancelled = false;
    setTermsLoading(true);
    setTermsError(null);
    adminUsersApi.getCustomerTermsAcceptances(user.subjectId, auth.token)
      .then((list) => { if (!cancelled) setTermsAcceptances(list); })
      .catch((err) => {
        if (cancelled) return;
        setTermsAcceptances([]);
        setTermsError(err instanceof Error ? err.message : 'Failed to load terms acceptance');
      })
      .finally(() => { if (!cancelled) setTermsLoading(false); });
    return () => { cancelled = true; };
  }, [auth.token, user?.subjectId, user?.userType]);

  /* ─── Actions ─── */
  const handleResetPassword = async () => {
    if (!auth.token || !user) return;
    setResetLoading(true);
    try {
      await adminUsersApi.resetPassword(user.subjectId, auth.token);
      showToast('Password reset email sent successfully', 'success');
      setShowResetConfirm(false);
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to send reset email', 'error');
    } finally {
      setResetLoading(false);
    }
  };

  const handleBlockClick = async () => {
    if (!auth.token || !user) return;
    if (user.userType === 'GARAGE_OWNER') {
      try {
        const warning = await adminUsersApi.getBlockWarning(user.id, auth.token);
        if (warning.activeOrderCount > 0) {
          setBlockWarningData(warning);
          setShowBlockWarning(true);
          return;
        }
      } catch {}
    }
    setShowBlockForm(true);
  };

  const handleBlock = async () => {
    if (!auth.token || !user || blockReason.length < 10) return;
    setBlockLoading(true);
    try {
      const updated = await adminUsersApi.blockUser(user.id, blockReason, auth.token);
      setUser(updated);
      setShowBlockForm(false);
      setShowBlockWarning(false);
      setBlockReason('');
      showToast('User blocked successfully', 'success');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to block user', 'error');
    } finally {
      setBlockLoading(false);
    }
  };

  const handleUnblock = async () => {
    if (!auth.token || !user) return;
    setBlockLoading(true);
    try {
      const updated = await adminUsersApi.unblockUser(user.id, auth.token);
      setUser(updated);
      showToast('User unblocked successfully', 'success');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to unblock user', 'error');
    } finally {
      setBlockLoading(false);
    }
  };

  /* ─── Render ─── */
  if (loading) return <div className="admin-loading"><div className="admin-loading-spinner"></div></div>;
  if (error) return <div className="error-message">{error}</div>;
  if (!user) return <div className="admin-empty-state"><p>User not found.</p></div>;

  const fmtDate = (d?: string) => d ? new Date(d).toLocaleString() : 'N/A';

  return (
    <div>
      {/* Toast */}
      {toast && (
        <div className={`admin-toast ${toast.type}`} style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, padding: '0.75rem 1.25rem', borderRadius: 8, color: '#fff', background: toast.type === 'success' ? '#16a34a' : '#dc2626', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.25rem' }}>
        <button className="admin-btn admin-btn-outline admin-btn-sm" onClick={() => navigate('/users')}>
          ← Back
        </button>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--admin-text)', margin: 0 }}>
          {user.name}
        </h2>
        {user.username && <UsernameChip username={user.username} />}
        <RoleBadge userType={user.userType} />
        <StatusBadge status={user.status} />
      </div>

      {/* Profile Section */}
      <div className="admin-card" style={{ marginBottom: '1.25rem' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>Profile Information</h3>
        <div className="admin-detail-grid">
          <Field label="Name" value={user.name} />
          <Field label="Username" value={user.username ? `@${user.username}` : '—'} />
          <Field label="Email" value={user.email} />
          <Field label="Phone" value={user.phone} />
          <Field label="Role" value={user.userType.replace('_', ' ')} />
          <Field label="Status" value={user.status} />
          <Field label="Language" value={user.languagePreference} />
          <Field label="Onboarding" value={user.onboardingCompleted ? 'Completed' : 'Incomplete'} />
          <Field label="Registered" value={fmtDate(user.createdAt)} />
          {user.userType !== 'CUSTOMER' && (
            <Field label="Terms Accepted" value={user.termsAcceptedAt ? fmtDate(user.termsAcceptedAt) : 'Not accepted'} />
          )}
          <Field label="Partnership Policy" value={user.partnershipPolicyAcceptedAt ? fmtDate(user.partnershipPolicyAcceptedAt) : 'Not accepted'} />
        </div>
      </div>

      {/* ─── CUSTOMER: Terms acceptance (#1165) ─── */}
      {user.userType === 'CUSTOMER' && (
        <div className="admin-card" style={{ marginBottom: '1.25rem' }} data-testid="customer-terms-acceptance">
          <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>Terms acceptance</h3>
          {termsLoading ? (
            <div className="admin-loading"><div className="admin-loading-spinner"></div></div>
          ) : termsError ? (
            <p role="alert" style={{ color: '#dc2626', fontSize: '0.85rem', margin: 0 }}>
              Could not load terms acceptance: {termsError}
            </p>
          ) : termsAcceptances.length === 0 ? (
            <div style={{ fontWeight: 500, color: 'var(--admin-text)' }}>Not accepted</div>
          ) : (
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead><tr><th>Document</th><th>Version</th><th>Accepted</th></tr></thead>
                <tbody>
                  {termsAcceptances.map((a) => (
                    <tr key={`${a.documentId}-${a.version}`}>
                      <td style={{ fontWeight: 500 }}>{a.title}</td>
                      <td>
                        v{a.version}
                        {a.currentVersion != null && a.currentVersion > a.version && (
                          <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}>
                            newer version v{a.currentVersion} published
                          </div>
                        )}
                      </td>
                      <td>{fmtDate(a.acceptedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Admin Actions Panel */}
      <div className="admin-card" style={{ marginBottom: '1.25rem' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>Admin Actions</h3>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <button className="admin-btn admin-btn-outline" onClick={() => setShowResetConfirm(true)}>
            Reset Password
          </button>
          {user.userType !== 'ADMIN' && user.status === 'ACTIVE' && (
            <button className="admin-btn admin-btn-danger" onClick={handleBlockClick}>
              Block User
            </button>
          )}
          {user.status === 'BLOCKED' && (
            <button className="admin-btn admin-btn-success" onClick={handleUnblock} disabled={blockLoading}>
              {blockLoading ? 'Processing...' : 'Unblock User'}
            </button>
          )}
        </div>
      </div>

      {/* Block History */}
      {user.blockRecords && user.blockRecords.length > 0 && (
        <div className="admin-card" style={{ marginBottom: '1.25rem' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>Block History</h3>
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead><tr><th>Action</th><th>Reason</th><th>Admin</th><th>Date</th></tr></thead>
              <tbody>
                {user.blockRecords.map((r) => (
                  <tr key={r.id}>
                    <td><span className={`admin-badge ${r.action === 'BLOCKED' ? 'rejected' : 'approved'}`}>{r.action}</span></td>
                    <td>{r.reason || '-'}</td>
                    <td>{r.adminName}</td>
                    <td>{fmtDate(r.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ─── PARTNER ROLES: Linked Garages ─── */}
      {(user.userType === 'GARAGE_OWNER'
          || user.userType === 'GARAGE_SUPPORT'
          || user.userType === 'GARAGE_MANAGER') && (
        <div className="admin-card" style={{ marginBottom: '1.25rem' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>Linked Garages</h3>
          {garagesLoading ? (
            <div className="admin-loading"><div className="admin-loading-spinner"></div></div>
          ) : garages.length === 0 ? (
            <div className="admin-empty-state"><p>Not linked to any garage.</p></div>
          ) : (
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead><tr><th>Name</th><th>Status</th><th>City</th><th></th></tr></thead>
                <tbody>
                  {garages.map((g) => (
                    <tr key={g.id}>
                      <td style={{ fontWeight: 500 }}>{g.name}</td>
                      <td><span className={`admin-badge ${g.status?.toLowerCase()}`}>{g.status}</span></td>
                      <td>{g.city || '-'}</td>
                      <td><Link to={`/garages/${g.id}`} className="admin-btn admin-btn-outline admin-btn-sm">View</Link></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ─── CUSTOMER: Vehicles ─── */}
      {user.userType === 'CUSTOMER' && (
        <div className="admin-card" style={{ marginBottom: '1.25rem' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>Vehicles</h3>
          {vehiclesLoading ? (
            <div className="admin-loading"><div className="admin-loading-spinner"></div></div>
          ) : vehicles.length === 0 ? (
            <div className="admin-empty-state"><p>No vehicles on file</p></div>
          ) : (
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead><tr><th>Make &amp; Model</th><th>Year</th><th>Plate</th><th>Color</th></tr></thead>
                <tbody>
                  {vehicles.map((v) => (
                    <tr key={v.id}>
                      <td style={{ fontWeight: 500 }}>
                        {[v.make, v.model].filter(Boolean).join(' ') || v.label || '—'}
                        {v.isDefault && <span className="vehicle-default-chip">Default</span>}
                      </td>
                      <td>{v.year ?? '—'}</td>
                      <td>{v.plateNumber || '—'}</td>
                      <td>{v.color || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ─── CUSTOMER: Order History ─── */}
      {user.userType === 'CUSTOMER' && (
        <div className="admin-card" style={{ marginBottom: '1.25rem' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>Order History</h3>
          <div className="admin-toolbar" style={{ gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
            <input type="text" className="admin-search-input" placeholder="Search by order ID..." value={orderSearch} onChange={(e) => setOrderSearch(e.target.value)} style={{ maxWidth: 200 }} />
            <select className="admin-select" value={orderStatusFilter} onChange={(e) => setOrderStatusFilter(e.target.value)}>
              <option value="">All Statuses</option>
              {ORDER_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
            </select>
            <input type="date" className="admin-form-input" value={orderDateFrom} onChange={(e) => setOrderDateFrom(e.target.value)} style={{ maxWidth: 160 }} />
            <input type="date" className="admin-form-input" value={orderDateTo} onChange={(e) => setOrderDateTo(e.target.value)} style={{ maxWidth: 160 }} />
          </div>
          {ordersLoading ? (
            <div className="admin-loading"><div className="admin-loading-spinner"></div></div>
          ) : orders.length === 0 ? (
            <div className="admin-empty-state"><p>No orders found.</p></div>
          ) : (
            <>
              <div className="admin-table-wrapper">
                <table className="admin-table">
                  <thead><tr><th>Order ID</th><th>Status</th><th>Amount</th><th>Date</th><th></th></tr></thead>
                  <tbody>
                    {orders.map((o) => (
                      <tr key={o.orderId}>
                        <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{o.orderId.substring(0, 8)}...</td>
                        <td><OrderStatusBadge status={o.status} /></td>
                        <td>{o.totalAmount?.toFixed(2)} {o.currency}</td>
                        <td>{new Date(o.createdAt).toLocaleDateString()}</td>
                        <td><Link to={`/orders/${o.orderId}`} className="admin-btn admin-btn-outline admin-btn-sm">View</Link></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <CursorPagination hasMore={ordersHasMore} loading={ordersLoadingMore} onLoadMore={() => fetchOrders(ordersNextCursor)} />
            </>
          )}
        </div>
      )}

      {/* ─── ADMIN: Action History (Spec 069: unified audit, catalogue labels) ─── */}
      {user.userType === 'ADMIN' && <AdminActionsCard userId={user.id} subjectId={user.subjectId} />}

      {/* ─── MODALS ─── */}

      {/* Password Reset Confirmation */}
      {showResetConfirm && (
        <div className="admin-modal-overlay" onClick={() => setShowResetConfirm(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header"><h3>Reset Password</h3></div>
            <div className="admin-modal-body">
              <p>Send a password reset email to <strong>{user.email || user.name}</strong>?</p>
            </div>
            <div className="admin-modal-footer">
              <button className="admin-btn admin-btn-outline" onClick={() => setShowResetConfirm(false)}>Cancel</button>
              <button className="admin-btn" onClick={handleResetPassword} disabled={resetLoading}>
                {resetLoading ? 'Sending...' : 'Send Reset Email'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Block Warning (garage owner with active orders) */}
      {showBlockWarning && blockWarningData && (
        <div className="admin-modal-overlay" onClick={() => setShowBlockWarning(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header"><h3>Warning: Active Orders</h3></div>
            <div className="admin-modal-body">
              <p style={{ color: '#d97706', fontWeight: 500, marginBottom: '0.75rem' }}>
                This garage owner has <strong>{blockWarningData.activeOrderCount}</strong> active order(s) across their garages:
              </p>
              <ul style={{ marginBottom: '1rem' }}>
                {blockWarningData.garageNames.map((name, i) => <li key={i}>{name}</li>)}
              </ul>
              <p>Blocking will prevent the owner from managing their garages, but active orders will not be cancelled.</p>
              <p style={{ marginTop: '0.75rem' }}>Do you want to proceed with blocking?</p>
            </div>
            <div className="admin-modal-footer">
              <button className="admin-btn admin-btn-outline" onClick={() => setShowBlockWarning(false)}>Cancel</button>
              <button className="admin-btn admin-btn-danger" onClick={() => { setShowBlockWarning(false); setShowBlockForm(true); }}>
                Proceed to Block
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Block Form */}
      {showBlockForm && (
        <div className="admin-modal-overlay" onClick={() => setShowBlockForm(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header"><h3>Block User</h3></div>
            <div className="admin-modal-body">
              <p style={{ marginBottom: '0.75rem' }}>Blocking <strong>{user.name}</strong> will prevent them from accessing the platform.</p>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Reason for blocking *</label>
              <textarea
                className="admin-textarea"
                value={blockReason}
                onChange={(e) => setBlockReason(e.target.value)}
                placeholder="Provide a reason (min 10 characters)..."
                rows={4}
              />
              {blockReason.length > 0 && blockReason.length < 10 && (
                <p style={{ color: '#dc2626', fontSize: '0.85rem', marginTop: 4 }}>Reason must be at least 10 characters.</p>
              )}
            </div>
            <div className="admin-modal-footer">
              <button className="admin-btn admin-btn-outline" onClick={() => { setShowBlockForm(false); setBlockReason(''); }}>Cancel</button>
              <button className="admin-btn admin-btn-danger" onClick={handleBlock} disabled={blockLoading || blockReason.length < 10}>
                {blockLoading ? 'Blocking...' : 'Confirm Block'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
